import React, { useState, useEffect } from 'react';
import { X, Star, Quote } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { reviewsAPI } from '../../services/api';

const PAGE_SIZE = 10;

function timeAgo(isoDate) {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 1) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

/**
 * AllReviewsModal — full paginated review list for a target.
 *
 * Props:
 *   isOpen       boolean
 *   onClose      () => void
 *   targetType   'individual' | 'academy' | 'tournament'
 *   targetId     string | number
 *   targetName   string — shown in the modal title
 */
const AllReviewsModal = ({ isOpen, onClose, targetType, targetId, targetName }) => {
  const { theme } = useTheme();
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ averageRating: null, totalReviews: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
  }, [isOpen, targetType, targetId]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    reviewsAPI.listForTarget(targetType, targetId, { page, limit: PAGE_SIZE })
      .then(res => {
        if (cancelled) return;
        setReviews(res.data || []);
        setSummary(res.summary || { averageRating: null, totalReviews: 0 });
        setTotalPages(res.pagination?.totalPages || 1);
      })
      .catch(err => console.error('Failed to load reviews:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, targetType, targetId, page]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  /**
   * ESCAPE CLOSES IT.
   *
   * There was no key handler, and the close button's icon was the same colour
   * as the card behind it — so this dialog had no working way out at all.
   * Bound only while open, and removed on unmount.
   */
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      // The dimmed area closes it too — the third thing people try, after
      // Escape and the button.
      onClick={onClose}
    >
      <div
        className={`${theme.card} rounded-lg max-w-2xl w-full h-[80vh] max-h-[700px] overflow-hidden flex flex-col my-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between p-4 border-b ${theme.border}`}>
          <div>
            <h3 className={`text-lg font-semibold ${theme.text}`}>
              Reviews{targetName ? ` for ${targetName}` : ''}
            </h3>
            {summary.averageRating != null && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <Star size={14} className="text-yellow-400 fill-yellow-400" />
                <span className={`text-sm font-medium ${theme.text}`}>{summary.averageRating}</span>
                <span className={`text-xs ${theme.textMuted}`}>
                  ({summary.totalReviews} review{summary.totalReviews === 1 ? '' : 's'})
                </span>
              </div>
            )}
          </div>
          {/* Coloured explicitly: with no class of its own the icon inherited a
              colour that matched the card, leaving a button nobody could see. */}
          <button
            onClick={onClose}
            aria-label="Close"
            className={`p-2 ${theme.cardHover} rounded-full transition-colors`}
          >
            <X className={`w-5 h-5 ${theme.text}`} />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto min-h-0 space-y-3">
          {loading ? (
            [0, 1, 2].map(i => <div key={i} className={`${theme.backgroundSecondary} rounded-lg h-24 animate-pulse`} />)
          ) : reviews.length === 0 ? (
            <div className="text-center py-10">
              <Quote className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className={theme.textMuted}>No reviews yet</p>
            </div>
          ) : (
            reviews.map(r => (
              <div key={r.id} className={`${theme.backgroundSecondary} rounded-lg p-4`}>
                <div className="flex items-center gap-3 mb-2">
                  {r.reviewer?.profileImage ? (
                    <img src={r.reviewer.profileImage} alt={r.reviewer.firstName} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 font-semibold text-xs flex-shrink-0">
                      {(r.reviewer?.firstName?.[0] || '?').toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className={`font-medium text-sm ${theme.text} truncate`}>
                      {[r.reviewer?.firstName, r.reviewer?.lastName].filter(Boolean).join(' ') || 'Anonymous'}
                    </div>
                    <div className={`text-xs ${theme.textMuted}`}>{timeAgo(r.createdAt)}</div>
                  </div>
                  <div className="flex gap-0.5 flex-shrink-0">
                    {Array.from({ length: r.rating }).map((_, i) => (
                      <Star key={i} size={12} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                <p className={`text-sm ${theme.textSecondary}`}>{r.reviewText}</p>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className={`flex items-center justify-center gap-3 p-3 border-t ${theme.border}`}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className={`px-3 py-1.5 text-sm rounded-lg ${theme.backgroundSecondary} ${theme.text} disabled:opacity-40`}
            >
              Previous
            </button>
            <span className={`text-sm ${theme.textMuted}`}>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className={`px-3 py-1.5 text-sm rounded-lg ${theme.backgroundSecondary} ${theme.text} disabled:opacity-40`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllReviewsModal;
